import { useCallback, useEffect, useRef } from 'react';
import type { TFunction } from 'i18next';
import type { U2000Pagination } from '../components/tables/basic/U2000BasicTable';
import type { U2000TableColumn } from '../components/tables/basic/U2000BasicTableList';
import type { QuickSearchData } from '../context/logicalUnit/U2000LogicalUnitContext';
import { U2000GridStateEnumPaginationDirection } from '../reducer/formReducer/U2000GridState';
import type { U2000GridStateHandlers } from '../reducer/formReducer/U2000GridStateHandlers';
import type { U2000SearchParameter } from '../reducer/formReducer/U2000SearchState';
import type { U2000SearchStateHandlers } from '../reducer/formReducer/U2000SearchStateHandlers';
import {
  getConditionForField,
  u2000BuildQuickSearchConsumeToken,
  u2000BuildQuickSearchSearchParameter,
  u2000ShouldConsumeQuickSearchToken,
  type U2000QuickSearchSession,
} from '../tools/U2000QuickSearchUtils';

export interface UseU2000QuickSearchListEffectParams {
  targetLogicalUnit: string;
  t: TFunction;
  searchStateHandlers: U2000SearchStateHandlers;
  gridStateHandlers: Pick<U2000GridStateHandlers<Record<string, unknown>>, 'extractPagination'>;
  runSearch: (
    searchParameter: U2000SearchParameter,
    pagination: U2000Pagination,
    direction: U2000GridStateEnumPaginationDirection
  ) => void | Promise<void>;
  quickSearchData: QuickSearchData | null;
  setQuickSearchData: (data: QuickSearchData | null) => void;
}

export interface UseU2000QuickSearchListEffectReturn {
  /**
   * Lit et efface la session QS capturée (pour onSuccess : navigation 1 hit).
   * Retourne null si aucune session en attente.
   */
  consumeQuickSearchSession: () => U2000QuickSearchSession | null;
}

/**
 * Consomme quickSearchData une seule fois par jeton et lance la recherche liste.
 * Le POST utilise un payload QS explicite (un critère), indépendant de l’état React encore non commité.
 */
export function useU2000QuickSearchListEffect({
  targetLogicalUnit,
  t,
  searchStateHandlers,
  gridStateHandlers,
  runSearch,
  quickSearchData,
  setQuickSearchData,
}: UseU2000QuickSearchListEffectParams): UseU2000QuickSearchListEffectReturn {
  const processingTokenRef = useRef<string | null>(null);
  const sessionRef = useRef<U2000QuickSearchSession | null>(null);

  const searchHandlersRef = useRef(searchStateHandlers);
  searchHandlersRef.current = searchStateHandlers;

  const gridHandlersRef = useRef(gridStateHandlers);
  gridHandlersRef.current = gridStateHandlers;

  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  const tRef = useRef(t);
  tRef.current = t;

  const setQuickSearchDataRef = useRef(setQuickSearchData);
  setQuickSearchDataRef.current = setQuickSearchData;

  const consumeQuickSearchSession = useCallback((): U2000QuickSearchSession | null => {
    const session = sessionRef.current;
    sessionRef.current = null;
    return session;
  }, []);

  const columnsReady = searchStateHandlers.getColumns().length > 0;

  useEffect(() => {
    if (!quickSearchData) {
      processingTokenRef.current = null;
      return;
    }

    if (!columnsReady) {
      return;
    }

    if (quickSearchData.targetLogicalUnit !== targetLogicalUnit) {
      return;
    }

    const token = u2000BuildQuickSearchConsumeToken(quickSearchData);
    if (!u2000ShouldConsumeQuickSearchToken(token, processingTokenRef.current)) {
      return;
    }
    processingTokenRef.current = token;

    sessionRef.current = {
      isFromQuickSearch: quickSearchData.isFromQuickSearch === true,
      targetLogicalUnit: quickSearchData.targetLogicalUnit ?? targetLogicalUnit,
    };

    const handlers = searchHandlersRef.current;
    const columns = handlers.getColumns();
    const field = columns.find((col) => col.name === quickSearchData.fieldName);

    if (!field) {
      sessionRef.current = null;
      setQuickSearchDataRef.current(null);
      return;
    }

    const condition = getConditionForField(field.type, quickSearchData.condition, field as U2000TableColumn, tRef.current);
    if (!condition) {
      sessionRef.current = null;
      setQuickSearchDataRef.current(null);
      return;
    }

    const criteriaValue = quickSearchData.value;

    setQuickSearchDataRef.current(null);

    const currentFields = handlers.getFields();
    if (currentFields.length === 0) {
      handlers.setFields(
        handlers
          .getColumns()
          .filter((col: U2000TableColumn) => col.isDefaultColumn)
          .map((col: U2000TableColumn) => ({ field: { ...col } }))
      );
    }

    const currentSortedFields = handlers.getSortedFields();
    if (currentSortedFields.length === 0) {
      handlers.setSortedFields(
        handlers
          .getColumns()
          .filter((col: U2000TableColumn) => col.isDefaultSortedColumn)
          .map((col: U2000TableColumn) => ({ field: { ...col }, sortOrder: 'ASC' }))
      );
    }

    handlers.selectFilterId(null);
    handlers.setCriterias([]);
    handlers.addOrUpdateCriterias(field, condition, criteriaValue);

    const searchParameter = u2000BuildQuickSearchSearchParameter(
      handlers.extractSearchParameter(),
      field as U2000TableColumn,
      condition.name,
      criteriaValue,
    );

    runSearchRef.current(
      searchParameter,
      gridHandlersRef.current.extractPagination(1),
      U2000GridStateEnumPaginationDirection.NEXTPAGE
    );
  }, [quickSearchData, columnsReady, targetLogicalUnit]);

  return { consumeQuickSearchSession };
}

export default useU2000QuickSearchListEffect;
